<script setup>
import { Head, Link, usePage, router } from '@inertiajs/vue3';
import { ref, computed } from 'vue';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';

const page = usePage();

const props = defineProps({
    setting: Object,
    news: Object,
    processedContent: String,
    relatedNews: Array,
    latestNews: Array,
    popularTags: Array,
    archives: Array,
    categories: Array, // Added for consistency with Sidebar
    title: String,
    meta_description: String,
    meta_keywords: String,
    canonical_url: String, // Restored
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
});

const searchQuery = ref('');
const handleSearch = () => {
    router.visit('/berita-dan-artikel', {
        method: 'get',
        data: { search: searchQuery.value },
    });
};

const formatDate = (dateString, mode = 'long') => {
    if (!dateString) return '';
    const options = mode === 'short' 
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat('id-ID', options).format(new Date(dateString));
};

const getMonthName = (monthNumber) => {
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(date);
};

const getImageUrl = (image) => image ? `/storage/news/${image}` : '/front/img/default-news.jpg';

// JSON-LD Schema
const jsonLd = computed(() => {
    const appUrl = usePage().props.app_url || window.location.origin;
    const currentUrl = props.canonical_url || window.location.href;
    const logoUrl = props.setting?.setting_logo ? `/storage/logo/${props.setting.setting_logo}` : '/front/img/logo.png';
    const imageUrl = props.news.image ? `${appUrl}/storage/news/${props.news.image}` : `${appUrl}/front/img/default-news.jpg`;

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Article",
                "headline": props.news.title,
                "description": props.meta_description,
                "image": imageUrl,
                "datePublished": props.news.published_at,
                "dateModified": props.news.updated_at || props.news.published_at,
                "author": {
                    "@type": "Person",
                    "name": props.news.author || 'Admin'
                },
                "publisher": {
                    "@type": "Organization",
                    "name": props.setting?.setting_title || 'Gotransport Banyuwangi',
                    "url": appUrl,
                    "logo": {
                        "@type": "ImageObject",
                        "url": `${appUrl}${logoUrl}`
                    }
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": currentUrl
                }
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { 
                        "@type": "ListItem", 
                        "position": 1, 
                        "name": "Beranda", 
                        "item": appUrl 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 2, 
                        "name": "Berita & Artikel", 
                        "item": `${appUrl}/berita-dan-artikel` 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 3, 
                        "name": props.news.title, 
                        "item": currentUrl 
                    }
                ]
            }
        ]
    };
});
</script>

<template>
    <FrontLayout 
        :setting="setting"
        :title="title"
        :metaDescription="meta_description"
        :metaKeywords="meta_keywords"
        :ogTitle="og_title"
        :ogDescription="og_description"
        :ogImage="og_image"
        :ogUrl="og_url"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            <meta name="author" :content="news.author || 'Admin'" />
            <meta name="article:published_time" :content="news.published_at" />
            <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <!-- Use PageHero but with simpler config for detail page -->
        <PageHero 
            :title="news.title"
            :subtitle="`Diposting pada ${formatDate(news.published_at)}`"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Berita', url: '/berita-dan-artikel' },
                { label: 'Detail' }
            ]"
        />

        <section class="py-12 bg-gray-50 dark:bg-gray-900 min-h-[600px]">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <!-- Featured Image -->
                            <div class="w-full">
                                <img :src="getImageUrl(news.image)" :alt="news.title" class="w-full h-auto">
                            </div>

                            <div class="p-4">
                                <!-- Meta header -->
                                <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                                    <span class="flex items-center gap-2">
                                        <i class="fa fa-calendar text-orange-500"></i> {{ formatDate(news.published_at) }}
                                    </span>
                                    <span class="flex items-center gap-2">
                                        <i class="fa fa-user text-orange-500"></i> {{ news.author || 'Admin' }}
                                    </span>
                                    <span class="flex items-center gap-2" v-if="news.category_model">
                                        <i class="fa fa-folder text-orange-500"></i> {{ news.category_model.name }}
                                    </span>
                                    <span class="flex items-center gap-2">
                                        <i class="fa fa-eye text-orange-500"></i> {{ news.views || 0 }} Views
                                    </span>
                                </div>

                                <!-- Content -->
                                <div class="prose dark:prose-invert max-w-none text-sm prose-orange prose-img:rounded-xl prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-a:text-blue-600 hover:prose-a:text-blue-700 transition-colors prose-p:my-1.5 prose-headings:my-3 prose-li:my-0"
                                     v-html="processedContent || news.content">
                                </div>

                                <!-- Tags -->
                                <div class="mt-8 pt-8 border-t border-gray-100 dark:border-gray-700" v-if="news.tags && news.tags.length">
                                    <div class="flex flex-wrap gap-2 items-center">
                                        <span class="text-sm font-bold text-gray-700 dark:text-gray-300 mr-2"><i class="fa fa-tags text-orange-500"></i> Tags:</span>
                                        <Link v-for="tag in news.tags" :key="tag.id" :href="`/berita-dan-artikel/tag/${tag.slug}`" 
                                              class="px-3 py-1 bg-gray-50 dark:bg-gray-700 hover:bg-orange-500 hover:text-white text-gray-600 dark:text-gray-300 text-xs font-semibold rounded-full border border-gray-200 dark:border-gray-600 hover:border-orange-500 transition-all">
                                            #{{ tag.name }}
                                        </Link>
                                    </div>
                                </div>

                                <!-- Share -->
                                <div class="mt-8">
                                    <SocialShare :url="canonical_url" :title="news.title" />
                                </div>
                            </div>
                        </article>

                        <!-- Related News -->
                        <div class="mt-12" v-if="relatedNews && relatedNews.length">
                            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                                <span class="w-1.5 h-8 bg-orange-600 rounded-full"></span> Artikel Terkait
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div v-for="item in relatedNews" :key="item.id" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all group">
                                    <div class="aspect-[4/3] overflow-hidden">
                                        <Link :href="`/artikel/${item.slug}`">
                                            <img :src="getImageUrl(item.image)" :alt="item.title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
                                        </Link>
                                    </div>
                                    <div class="p-4">
                                        <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2"><i class="fa fa-calendar"></i> {{ formatDate(item.published_at, 'short') }}</div>
                                        <h4 class="font-bold text-gray-900 dark:text-white line-clamp-2 leading-snug group-hover:text-orange-600 transition-colors">
                                            <Link :href="`/artikel/${item.slug}`">{{ item.title }}</Link>
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sidebar -->
                    <aside class="lg:col-span-1 space-y-8">
                        <!-- Search Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-search text-orange-500"></i> Cari Artikel
                             </h3>
                             <form @submit.prevent="handleSearch" class="relative">
                                 <input v-model="searchQuery" type="text" placeholder="Kata kunci..." class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-100 dark:focus:ring-orange-900 focus:border-orange-400 dark:focus:border-orange-500 transition-all text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400">
                                 <button type="submit" class="absolute right-2 top-2 p-1.5 text-gray-400 hover:text-orange-600 transition-colors">
                                     <i class="fa fa-search"></i>
                                 </button>
                             </form>
                        </div>

                         <!-- Categories Widget -->
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="categories && categories.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-folder text-orange-500"></i> Kategori
                             </h3>
                             <div class="flex flex-col">
                                 <Link v-for="(cat, idx) in categories" :key="cat.id" :href="`/berita-dan-artikel/kategori/${cat.slug}`" 
                                       class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:pl-2 transition-all duration-300 group">
                                     <div class="flex items-center gap-3">
                                         <i class="fa fa-folder text-gray-400 group-hover:text-orange-500 transition-colors"></i>
                                         <span class="text-gray-600 dark:text-gray-300 font-medium text-sm group-hover:text-orange-600 transition-colors">{{ cat.name }}</span>
                                     </div>
                                     <span class="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs py-1 px-2 rounded group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 group-hover:text-orange-600 transition-colors">{{ cat.news_count }}</span>
                                 </Link>
                             </div>
                        </div>

                        <!-- Latest News Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="latestNews && latestNews.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-bolt text-orange-500"></i> Terbaru
                             </h3>
                             <div class="space-y-4">
                                 <div v-for="item in latestNews" :key="item.id" class="flex gap-3 group">
                                     <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                         <Link :href="`/artikel/${item.slug}`">
                                             <img :src="getImageUrl(item.image)" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" :alt="item.title">
                                         </Link>
                                     </div>
                                     <div class="flex-1">
                                         <h4 class="text-sm font-bold text-gray-900 dark:text-white leading-snug group-hover:text-orange-600 transition-colors line-clamp-2 mb-1">
                                             <Link :href="`/artikel/${item.slug}`">{{ item.title }}</Link>
                                         </h4>
                                         <span class="text-xs text-gray-400"><i class="fa fa-clock-o mr-1"></i> {{ formatDate(item.published_at, 'short') }}</span>
                                     </div>
                                 </div>
                             </div>
                        </div>

                        <!-- Archive Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="archives && archives.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-archive text-orange-500"></i> Arsip
                             </h3>
                             <div class="flex flex-col">
                                 <Link v-for="(archive, idx) in archives" :key="idx" :href="`/berita-dan-artikel/arsip/${archive.year}/${archive.month}`"
                                       class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:pl-2 transition-all duration-300 group">
                                     <div class="flex items-center gap-3">
                                         <i class="fa fa-calendar text-gray-400 group-hover:text-orange-500 transition-colors"></i>
                                         <span class="text-gray-600 dark:text-gray-300 font-medium text-sm group-hover:text-orange-600 transition-colors">{{ getMonthName(archive.month) }} {{ archive.year }}</span>
                                     </div>
                                     <span class="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs py-1 px-2 rounded group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 group-hover:text-orange-600 transition-colors">{{ archive.count }}</span>
                                 </Link>
                             </div>
                        </div>

                        <!-- Tags Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="popularTags && popularTags.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-tags text-orange-500"></i> Tag Populer
                             </h3>
                             <div class="flex flex-wrap gap-2">
                                 <Link v-for="t in popularTags" :key="t.id" :href="`/berita-dan-artikel/tag/${t.slug}`" 
                                       class="px-3 py-1 text-xs font-bold rounded-full border transition-all"
                                       :class="t.color ? 'text-black hover:opacity-80 shadow-sm' : 'bg-gray-50 dark:bg-gray-700 hover:bg-orange-500 hover:text-white text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-orange-500'"
                                       :style="t.color ? { backgroundColor: t.color, borderColor: t.color } : {}">
                                     #{{ t.name }}
                                 </Link>
                             </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
